import React, { useRef } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { InfoDataNine } from '../data/InfoData';
import { InfoDataTen } from '../data/InfoData';
import { InfoDataEleven } from '../data/InfoData';

/* ─── Fonts ──────────────────────────────────────────────────── */
const FontLoader = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Bebas+Neue&family=Jost:wght@300;400;500;600&display=swap');
`;

/* ─── Design Tokens ──────────────────────────────────────────── */
const gold    = '#c9a96e';
const cream   = '#f5f0e8';
const ink     = '#0e0c0a';
const mist    = '#1a1712';
const warm    = '#f9f5ee';
const charcoal= '#2a2420';
const bodyClr = 'rgba(40, 32, 24, 0.75)';
const display = `'Cormorant Garamond', Georgia, serif`;
const sans    = `'Jost', sans-serif`;
const slab    = `'Bebas Neue', sans-serif`;

/* ─── Keyframes ──────────────────────────────────────────────── */
const rotateSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const shimmerLine = keyframes`
  0%   { transform: scaleX(0); transform-origin: left; }
  100% { transform: scaleX(1); transform-origin: left; }
`;
const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;
const floatUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Motion Variants ────────────────────────────────────────── */
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.13 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};
const fadeIn = {
  hidden: { opacity: 0 },
  show:   { opacity: 1, transition: { duration: 0.9, ease: 'easeOut' } },
};

/* ─── Room Data ──────────────────────────────────────────────── */
const rooms = [
  { name: 'Love',  num: 'I',   verse: '1 Corinthians 13:4' },
  { name: 'Faith', num: 'II',  verse: 'Hebrews 11:1'       },
  { name: 'Grace', num: 'III', verse: 'Ephesians 2:8'      },
  { name: 'Peace', num: 'IV',  verse: 'Philippians 4:7'    },
  { name: 'Joy',   num: 'V',   verse: 'Psalm 16:11'        },
  { name: 'Hope',  num: 'VI',  verse: 'Romans 15:13'       },
];

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
const Ourstoryscreen = () => {
  const heroRef  = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY    = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  /* Pull images from data objects — supports .img or .image conventions */
  const img2 = InfoDataTen?.img   || InfoDataTen?.image   || '';
  const img3 = InfoDataEleven?.img|| InfoDataEleven?.image|| '';

  return (
    <>
      <FontLoader />
      <PageWrapper>

        {/* ── Dark Hero Masthead ─────────────────────────────── */}
        <Masthead ref={heroRef}>
          <MastheadNoise />
          <MastheadGlow />
          <MastheadInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            animate="show"
            style={{ y: heroY, opacity: heroOpacity }}
          >
            <EyebrowRow as={motion.div} variants={fadeUp}>
              <EyebrowLine />
              <Eyebrow>Little Heaven Bed &amp; Breakfast</Eyebrow>
              <EyebrowLine flipped />
            </EyebrowRow>
            <HeroTitle as={motion.div} variants={fadeUp}>
              Our<br /><em>Story</em>
            </HeroTitle>
            <HeroSub as={motion.div} variants={fadeUp}>
              A journey of faith, love, and finding Little Heaven
            </HeroSub>
            <HeroScroll as={motion.div} variants={fadeUp}>
              <ScrollLine />
              <ScrollLabel>Scroll</ScrollLabel>
            </HeroScroll>
          </MastheadInner>

          <RotatingBadge aria-hidden="true">
            <svg viewBox="0 0 100 100" width="116" height="116">
              <defs>
                <path id="circ2" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
              </defs>
              <text fontSize="11" fill={gold} fontFamily="Jost,sans-serif" fontWeight="500" letterSpacing="3.1">
                <textPath href="#circ2">ELKIN, NC · FAITH · LOVE · GRACE · </textPath>
              </text>
            </svg>
            <RotatingCenter>✦</RotatingCenter>
          </RotatingBadge>
        </Masthead>

        {/* ── Image Row ─────────────────────────────────────── */}
        <ImageRowSection>
          <ImageRowLabel
            as={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <ImageRowLabelText>Little Heaven</ImageRowLabelText>
            <ImageRowLabelLine />
          </ImageRowLabel>

          <ImageStrip
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
          >
            {[
              { src: img2, caption: 'The Experience',  alt: InfoDataTen?.alt   || 'Little Heaven experience'},
              { src: img3, caption: 'The Atmosphere',  alt: InfoDataEleven?.alt|| 'Little Heaven atmosphere'},
            ].map((img, i) => (
              <ImageCell key={i} as={motion.div} variants={fadeUp}>
                <ImageCellInner>
                  {img.src
                    ? <StripImg src={img.src} alt={img.alt} />
                    : <ImgPlaceholder><PlaceholderText>✦</PlaceholderText></ImgPlaceholder>
                  }
                  <ImageCellOverlay />
                </ImageCellInner>
                <ImageCaption>
                  <CaptionNum>0{i + 1}</CaptionNum>
                  <CaptionText>{img.caption}</CaptionText>
                </ImageCaption>
              </ImageCell>
            ))}
          </ImageStrip>
        </ImageRowSection>

        {/* ── Divider ───────────────────────────────────────── */}
        <OrnamentDivider>
          <DividerLine />
          <DividerCross>✦</DividerCross>
          <DividerLine />
        </OrnamentDivider>

        {/* ── Chapter I: How It Began ───────────────────────── */}
        <ChapterSection>
          <ChapterSideLabel>
            <ChapterRoman>I</ChapterRoman>
            <ChapterSideTitle>How It Began</ChapterSideTitle>
          </ChapterSideLabel>

          <ChapterBody
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ChapterEyebrow as={motion.div} variants={fadeUp}>A Story of Faith</ChapterEyebrow>
            <ChapterTitle as={motion.div} variants={fadeUp}>
              Two Lives,<br /><em>One Purpose</em>
            </ChapterTitle>
            <ChapterDropCap as={motion.div} variants={fadeUp}>
              <DropCap>T</DropCap>
              <DropText>
                he story of how Little Heaven Bed &amp; Breakfast came into existence is one of
                Faith. Jonathan and Maria met each other in February 2016. They knew right away
                that they were meant to be together. By June 2016 they were married.
              </DropText>
            </ChapterDropCap>
            <ChapterPara as={motion.div} variants={fadeUp}>
              Maria had 4 daughters and Jonathan had 2 daughters when they first met. Neither of
              them had much growing up or really even when they met each other, but God changed all
              of that for them once they were together. They bought a few properties to try and do
              short term rentals and it really took off.
            </ChapterPara>

            <PullQuote as={motion.div} variants={fadeUp}>
              <PullQuoteBar />
              <PullQuoteText>
                "God changed all of that for them once they were together."
              </PullQuoteText>
            </PullQuote>
          </ChapterBody>
        </ChapterSection>

        {/* ── Full-Width Dark Break ─────────────────────────── */}
        <DarkBreak>
          <DarkBreakNoise />
          <DarkBreakGlow />
          <DarkBreakContent
            as={motion.div}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <DarkBreakEyebrow>The Dream</DarkBreakEyebrow>
            <DarkBreakTitle>
              A Little Piece<br /><em>of Heaven</em>
            </DarkBreakTitle>
            <DarkBreakSub>
              Pigeon Forge, Tennessee · Christmas Season
            </DarkBreakSub>
          </DarkBreakContent>
        </DarkBreak>

        {/* ── Chapter II: The Vision ────────────────────────── */}
        <ChapterSection $flip>
          <ChapterBody
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
          >
            <ChapterEyebrow as={motion.div} variants={fadeUp}>The Vision</ChapterEyebrow>
            <ChapterTitle as={motion.div} variants={fadeUp}>
              From Dream<br /><em>to Reality</em>
            </ChapterTitle>
            <ChapterPara as={motion.div} variants={fadeUp}>
              They started traveling and seeing what was out there and enjoyed it. One day while
              they were on vacation at Pigeon Forge Tennessee on Christmas Season, they had the
              thought about having their own bed and breakfast. They went to a couple of Bed &amp;
              Breakfasts to experience it and they fell in love with the idea.
            </ChapterPara>
            <ChapterPara as={motion.div} variants={fadeUp}>
              They sold two investment properties and their very first house to open what is now
              known as Little Heaven Bed &amp; Breakfast. They looked and prayed for several months
              to find the place until they found <em>'TUCKDA WAY'</em>. When they saw this place
              they immediately knew that it was THE ONE.
            </ChapterPara>
            <ChapterPara as={motion.div} variants={fadeUp}>
              The name <strong>LITTLE HEAVEN</strong> came from the beautiful views of mountains,
              blue heaven, the feel of the green forest, and of course the amazing views of acres
              of vineyard wineries surrounding the property.
            </ChapterPara>

            <TimelineRow
              as={motion.div}
              variants={stagger}
            >
              {[
                { year: 'Feb 2016', event: 'Jonathan & Maria meet' },
                { year: 'Jun 2016', event: 'United in marriage'    },
                { year: 'Dec 2021', event: 'Property purchased'    },
                { year: 'Today',    event: 'Little Heaven opens'   },
              ].map((t, i) => (
                <TimelineItem key={i} as={motion.div} variants={fadeUp}>
                  <TimelineYear>{t.year}</TimelineYear>
                  <TimelineDot />
                  <TimelineEvent>{t.event}</TimelineEvent>
                </TimelineItem>
              ))}
            </TimelineRow>

            <ChapterPara as={motion.div} variants={fadeUp}>
              They bought the cabin with 10 acres of land and a pond in December 2021. With many
              challenges and shortages of labor it took much longer than they thought. Each step of
              the way they relied on God and their Faith that it was all part of His plan.
            </ChapterPara>

            <PullQuote as={motion.div} variants={fadeUp}>
              <PullQuoteBar />
              <PullQuoteText>
                "One thing that Jonathan and Maria have always held strong to is their Faith through it all."
              </PullQuoteText>
            </PullQuote>
          </ChapterBody>

          <ChapterSideLabel $right>
            <ChapterRoman>II</ChapterRoman>
            <ChapterSideTitle>The Vision</ChapterSideTitle>
          </ChapterSideLabel>
        </ChapterSection>

        {/* ── Room Names ────────────────────────────────────── */}
        <RoomsSection>
          <RoomsNoise />
          <RoomsGlow />
          <RoomsInner>
            <RoomsHeader
              as={motion.div}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <RoomsEyebrow as={motion.div} variants={fadeUp}>Named After Scripture</RoomsEyebrow>
              <RoomsTitle as={motion.div} variants={fadeUp}>
                The Six<br /><em>Sacred Rooms</em>
              </RoomsTitle>
              <RoomsSub as={motion.div} variants={fadeUp}>
                Each room is named after the things that God spoke about in the Bible.
                Every name carries a promise, a truth, a gift.
              </RoomsSub>
            </RoomsHeader>

            <RoomsGrid
              as={motion.div}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-60px' }}
            >
              {rooms.map((room, i) => (
                <RoomCard
                  key={room.name}
                  as={motion.div}
                  variants={fadeUp}
                  whileHover={{ y: -8, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
                >
                  <RoomNum>{room.num}</RoomNum>
                  <RoomCardLine />
                  <RoomName>{room.name}</RoomName>
                  <RoomVerse>{room.verse}</RoomVerse>
                </RoomCard>
              ))}
            </RoomsGrid>
          </RoomsInner>
        </RoomsSection>

        {/* ── Closing Chapter ───────────────────────────────── */}
        <ClosingSection>
          <ClosingInner
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <ChapterEyebrow as={motion.div} variants={fadeUp} $light>An Invitation</ChapterEyebrow>
            <ClosingTitle as={motion.div} variants={fadeUp}>
              Come &amp; Experience<br /><em>Little Heaven</em>
            </ClosingTitle>
            <ClosingPara as={motion.div} variants={fadeUp}>
              These are the 6 rooms at Little Heaven Bed &amp; Breakfast. Each room has something
              a little different that you will love. We can't wait for you to come and experience
              the peacefulness of Little Heaven with us. We feel that once you experience Little
              Heaven, you <em>will</em> want to come back numerous times.
            </ClosingPara>
            <ClosingAccent as={motion.div} variants={fadeUp}>
              It's literally a Little Piece of Heaven.
            </ClosingAccent>
            <ClosingPara as={motion.div} variants={fadeUp}>
              We deeply thank you for your business and for choosing to stay with us.
              You will absolutely love your stay.
            </ClosingPara>
            <SignatureBlock as={motion.div} variants={fadeUp}>
              <SignatureLine />
              <SignatureName>Maria &amp; Jonathan Hunt</SignatureName>
              <SignatureTitle>Founders, Little Heaven Bed &amp; Breakfast</SignatureTitle>
            </SignatureBlock>
          </ClosingInner>
        </ClosingSection>

        {/* ── Marquee Strip ─────────────────────────────────── */}
        <MarqueeStrip>
          <MarqueeInner>
            {Array(8).fill('LITTLE HEAVEN · HAMPTONVILLE, NC · FAITH · LOVE · GRACE · PEACE · JOY · HOPE ·').map((t, i) => (
              <MarqueeText key={i}>{t}</MarqueeText>
            ))}
          </MarqueeInner>
        </MarqueeStrip>

      </PageWrapper>
    </>
  );
};

export default Ourstoryscreen;

/* ═══════════════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════════════ */

const PageWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
  background: ${warm};
  color: ${ink};
  font-family: ${sans};
`;

/* ── Masthead ─────────────────────────────────────────────────── */
const Masthead = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #141210 0%, #1e1a14 55%, #0e0c0a 100%);
  overflow: hidden;
  padding: 60px 24px 80px;
`;

const MastheadNoise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px;
  pointer-events: none;
`;

const MastheadGlow = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 65% 55% at 30% 40%, rgba(201,169,110,0.09) 0%, transparent 60%),
    radial-gradient(ellipse 45% 40% at 75% 65%, rgba(201,169,110,0.05) 0%, transparent 60%);
  pointer-events: none;
`;

const MastheadInner = styled(motion.div)`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 860px;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 32px;
`;

const EyebrowLine = styled.div`
  flex: 1;
  max-width: 90px;
  height: 1px;
  background: ${(p) => p.flipped
    ? `linear-gradient(90deg, ${gold}, transparent)`
    : `linear-gradient(90deg, transparent, ${gold})`};
`;

const Eyebrow = styled.span`
  font-family: ${sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  white-space: nowrap;
`;

const HeroTitle = styled.h1`
  font-family: ${display};
  font-size: clamp(5rem, 14vw, 13rem);
  font-weight: 300;
  line-height: 0.92;
  color: ${cream};
  margin: 0 0 32px;
  letter-spacing: -3px;

  em {
    font-style: italic;
    color: ${gold};
    display: block;
    font-size: 0.85em;
  }
`;

const HeroSub = styled.p`
  font-family: ${sans};
  font-size: clamp(0.88rem, 1.6vw, 1.05rem);
  font-weight: 300;
  letter-spacing: 2px;
  color: rgba(245,240,232,0.65);
  margin: 0 0 56px;
`;

const HeroScroll = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ScrollLine = styled.div`
  width: 1px;
  height: 52px;
  background: linear-gradient(180deg, transparent, ${gold});
`;

const ScrollLabel = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.6);
`;

const RotatingBadge = styled.div`
  position: absolute;
  bottom: 40px;
  right: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${rotateSlow} 20s linear infinite;

  @media (max-width: 768px) { display: none; }
`;

const RotatingCenter = styled.span`
  position: absolute;
  font-size: 1.3rem;
  color: ${gold};
  animation: ${rotateSlow} 20s linear infinite reverse;
`;

/* ── Image Row ────────────────────────────────────────────────── */
const ImageRowSection = styled.section`
  background: ${warm};
  padding: 72px 40px 60px;
  position: relative;

  @media (max-width: 768px) { padding: 56px 20px 48px; }
`;

const ImageRowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 32px;
`;

const ImageRowLabelText = styled.span`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  white-space: nowrap;
`;

const ImageRowLabelLine = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(201,169,110,0.5), transparent);
`;

const ImageStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`;

const ImageCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ImageCellInner = styled.div`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  aspect-ratio: 4/3;
  background: ${charcoal};
`;

const StripImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);

  ${ImageCellInner}:hover & { transform: scale(1.06); }
`;

const ImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2a2420, #3a3028);
`;

const PlaceholderText = styled.span`
  font-size: 3rem;
  color: rgba(201,169,110,0.25);
`;

const ImageCellOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 55%, rgba(14,12,10,0.45) 100%);
  pointer-events: none;
`;

const ImageCaption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CaptionNum = styled.span`
  font-family: ${slab};
  font-size: 1.4rem;
  color: rgba(201,169,110,0.35);
  line-height: 1;
`;

const CaptionText = styled.span`
  font-family: ${sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(40,32,24,0.5);
`;

/* ── Ornament Divider ─────────────────────────────────────────── */
const OrnamentDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 768px) { padding: 0 24px; }
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(201,169,110,0.25);
`;

const DividerCross = styled.div`
  font-size: 1rem;
  color: ${gold};
  opacity: 0.45;
  flex-shrink: 0;
`;

/* ── Chapter Sections ─────────────────────────────────────────── */
const ChapterSection = styled.section`
  display: grid;
  grid-template-columns: ${(p) => p.$flip ? '1fr 120px' : '120px 1fr'};
  gap: 0 20px;
  max-width: 1280px;
  margin: 0 auto;
  padding: 96px 40px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 72px 24px;
  }
`;

const ChapterSideLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => p.$right ? 'flex-end' : 'flex-start'};
  padding-top: 8px;
  border-${(p) => p.$right ? 'left' : 'right'}: 1px solid rgba(201,169,110,0.2);
  padding-${(p) => p.$right ? 'left' : 'right'}: 24px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const ChapterRoman = styled.div`
  font-family: ${slab};
  font-size: 5rem;
  color: rgba(201,169,110,0.18);
  line-height: 1;
  margin-bottom: 12px;
`;

const ChapterSideTitle = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.5);
  writing-mode: vertical-rl;
  transform: ${(p) => p.$right ? 'rotate(180deg)' : 'none'};
`;

const ChapterBody = styled.div`
  max-width: 780px;
`;

const ChapterEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${(p) => p.$light ? 'rgba(245,240,232,0.6)' : gold};
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 14px;

  &::before {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.45;
  }
`;

const ChapterTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 5.5vw, 5.5rem);
  font-weight: 300;
  line-height: 1.05;
  color: ${ink};
  margin: 0 0 40px;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const ChapterDropCap = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  align-items: flex-start;
`;

const DropCap = styled.span`
  font-family: ${display};
  font-size: 6.5rem;
  font-weight: 600;
  line-height: 0.78;
  color: ${gold};
  float: left;
  margin-right: 10px;
  margin-top: 8px;
`;

const DropText = styled.p`
  font-family: ${sans};
  font-size: 1.05rem;
  font-weight: 300;
  line-height: 1.95;
  color: ${bodyClr};
  margin: 0;
`;

const ChapterPara = styled.p`
  font-family: ${sans};
  font-size: 1.02rem;
  font-weight: 300;
  line-height: 1.95;
  color: ${bodyClr};
  margin: 0 0 24px;

  em { font-style: italic; color: ${ink}; font-weight: 400; }
  strong { color: ${ink}; font-weight: 600; }
`;

const PullQuote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin: 48px 0;
`;

const PullQuoteBar = styled.div`
  width: 3px;
  min-height: 80px;
  background: ${gold};
  border-radius: 2px;
  flex-shrink: 0;
`;

const PullQuoteText = styled.blockquote`
  font-family: ${display};
  font-size: clamp(1.3rem, 2.5vw, 1.9rem);
  font-style: italic;
  font-weight: 300;
  line-height: 1.55;
  color: ${ink};
  margin: 0;
  letter-spacing: 0.2px;
`;

/* ── Timeline ─────────────────────────────────────────────────── */
const TimelineRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  margin: 40px 0 40px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 24px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, ${gold}, rgba(201,169,110,0.2));
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px 0;
    &::before { display: none; }
  }
`;

const TimelineItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 4px;
`;

const TimelineYear = styled.div`
  font-family: ${slab};
  font-size: 0.95rem;
  letter-spacing: 1.5px;
  color: ${gold};
`;

const TimelineDot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 2px solid ${gold};
  background: ${warm};
  flex-shrink: 0;
`;

const TimelineEvent = styled.div`
  font-family: ${sans};
  font-size: 0.8rem;
  font-weight: 400;
  color: rgba(40,32,24,0.6);
  text-align: center;
  letter-spacing: 0.3px;
`;

/* ── Dark Break ───────────────────────────────────────────────── */
const DarkBreak = styled.section`
  position: relative;
  background: linear-gradient(160deg, #141210 0%, #0e0c0a 100%);
  min-height: 44vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 100px 24px;
  overflow: hidden;
`;

const DarkBreakNoise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.025;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px;
  pointer-events: none;
`;

const DarkBreakGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(201,169,110,0.08) 0%, transparent 65%);
  pointer-events: none;
`;

const DarkBreakContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
`;

const DarkBreakEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 5px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.7);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 28px;
    height: 1px;
    background: ${gold};
    opacity: 0.4;
  }
`;

const DarkBreakTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(4rem, 9vw, 9rem);
  font-weight: 300;
  line-height: 0.95;
  color: ${cream};
  margin: 0 0 22px;
  letter-spacing: -2px;

  em { font-style: italic; color: ${gold}; display: block; }
`;

const DarkBreakSub = styled.div`
  font-family: ${sans};
  font-size: 0.85rem;
  font-weight: 300;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(245,240,232,0.35);
`;

/* ── Rooms Section ────────────────────────────────────────────── */
const RoomsSection = styled.section`
  position: relative;
  background: linear-gradient(160deg, #1a1712 0%, #0e0c0a 100%);
  padding: 120px 40px 130px;
  overflow: hidden;

  @media (max-width: 768px) { padding: 90px 24px 100px; }
`;

const RoomsNoise = styled(MastheadNoise)``;
const RoomsGlow = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 50% 30%, rgba(201,169,110,0.07) 0%, transparent 60%);
  pointer-events: none;
`;

const RoomsInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1280px;
  margin: 0 auto;
`;

const RoomsHeader = styled.div`
  text-align: center;
  margin-bottom: 70px;
`;

const RoomsEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.75);
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.4;
  }
`;

const RoomsTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 6vw, 6rem);
  font-weight: 300;
  line-height: 1.0;
  color: ${cream};
  margin: 0 0 22px;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const RoomsSub = styled.p`
  font-family: ${sans};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.8;
  color: rgba(245,240,232,0.6);
  max-width: 620px;
  margin: 0 auto;
`;

const RoomsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;

  @media (max-width: 860px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const RoomCard = styled.div`
  position: relative;
  padding: 52px 40px 44px;
  background: linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
  border: 1px solid rgba(201,169,110,0.1);
  cursor: default;
  transition: background 0.4s, border-color 0.4s;
  text-align: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 80% 70% at 50% 100%, rgba(201,169,110,0.06) 0%, transparent 60%);
    opacity: 0;
    transition: opacity 0.4s;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(201,169,110,0.05), rgba(201,169,110,0.02));
    border-color: rgba(201,169,110,0.3);

    &::before { opacity: 1; }
  }
`;

const RoomNum = styled.div`
  font-family: ${slab};
  font-size: 3.5rem;
  color: rgba(201,169,110,0.15);
  line-height: 1;
  margin-bottom: 12px;
  letter-spacing: 2px;
`;

const RoomCardLine = styled.div`
  width: 32px;
  height: 1px;
  background: ${gold};
  opacity: 0.4;
  margin: 0 auto 20px;
  transition: width 0.4s, opacity 0.4s;

  ${RoomCard}:hover & {
    width: 56px;
    opacity: 0.8;
  }
`;

const RoomName = styled.h3`
  font-family: ${slab};
  font-size: 3rem;
  letter-spacing: 4px;
  color: ${cream};
  margin: 0 0 14px;
  line-height: 1;
  transition: color 0.3s;

  ${RoomCard}:hover & { color: ${gold}; }
`;

const RoomVerse = styled.div`
  font-family: ${display};
  font-size: 0.95rem;
  font-style: italic;
  color: rgba(201,169,110,0.5);
  letter-spacing: 0.5px;
`;

/* ── Closing Section ──────────────────────────────────────────── */
const ClosingSection = styled.section`
  background: ${warm};
  padding: 120px 40px;

  @media (max-width: 768px) { padding: 90px 24px; }
`;

const ClosingInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;

const ClosingTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 6vw, 6rem);
  font-weight: 300;
  line-height: 1.05;
  color: ${ink};
  margin: 0 0 40px;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const ClosingPara = styled.p`
  font-family: ${sans};
  font-size: 1.02rem;
  font-weight: 300;
  line-height: 1.95;
  color: ${bodyClr};
  margin: 0 0 28px;

  em { font-style: italic; color: ${ink}; font-weight: 400; }
`;

const ClosingAccent = styled.p`
  font-family: ${display};
  font-size: clamp(1.5rem, 3vw, 2.5rem);
  font-style: italic;
  font-weight: 300;
  color: ${gold};
  margin: 0 0 36px;
  letter-spacing: 0.3px;
`;

const SignatureBlock = styled.div`
  margin-top: 56px;
  padding-top: 40px;
  border-top: 1px solid rgba(201,169,110,0.25);
`;

const SignatureLine = styled.div`
  width: 80px;
  height: 2px;
  background: ${gold};
  margin-bottom: 18px;
  border-radius: 2px;
`;

const SignatureName = styled.div`
  font-family: ${display};
  font-size: 1.7rem;
  font-style: italic;
  font-weight: 400;
  color: ${ink};
  margin-bottom: 6px;
`;

const SignatureTitle = styled.div`
  font-family: ${sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(40,32,24,0.45);
`;

/* ── Marquee Strip ────────────────────────────────────────────── */
const MarqueeStrip = styled.div`
  background: ${gold};
  padding: 13px 0;
  overflow: hidden;
  white-space: nowrap;
`;

const MarqueeInner = styled.div`
  display: inline-flex;
  animation: ${marquee} 32s linear infinite;
`;

const MarqueeText = styled.span`
  font-family: ${slab};
  font-size: 0.95rem;
  letter-spacing: 3px;
  color: ${ink};
  padding: 0 24px;
`;